/**
 * BRIDGE — module RTK Query apis into the store (W2/W4).
 *
 * `shared/store` may not import a module, so the apis register THEMSELVES here
 * at import time and `createAppStore` picks up whatever the registry holds. This
 * file is one of exactly two places (with `app.config.tsx`) allowed to reach
 * into `modules/`.
 *
 * Importing an api module is what runs its `createApi` call, so the import is
 * the registration trigger — `registerApi` below makes that explicit rather than
 * relying on a side effect nobody can see.
 */
import { rolesApi } from '@/modules/Admins/services/roles/roles.api';
import {
  staffUsersApi,
  subscribeStaffUsers,
} from '@/modules/Admins/services/staff-users/staff-users.api';
import { authApi } from '@/modules/Auth/services/auth/auth.api';
import { coursesApi } from '@/modules/Courses/services/courses/courses.api';
import { dashboardStatsApi } from '@/modules/Dashboard/services/dashboard-stats/dashboard-stats.api';
import { membersApi, subscribeMembers } from '@/modules/Members/services/members/members.api';
import { legalApi } from '@/modules/Legal/services/legal/legal.api';
import { meetingsApi } from '@/modules/Meetings/services/meetings/meetings.api';
import { integrationsApi } from '@/modules/Integrations/services/integrations/integrations.api';
import { moderationApi } from '@/modules/Moderation/services/moderation/moderation.api';
import { billingEventsApi } from '@/modules/Payments/services/billing-events/billing-events.api';
import { entitlementsApi } from '@/modules/Payments/services/entitlements/entitlements.api';
import { paymentsApi } from '@/modules/Payments/services/payments/payments.api';
import { iapOfferCodesApi } from '@/modules/Subscriptions/services/iap-offer-codes/iap-offer-codes.api';
import { subscriptionsApi } from '@/modules/Subscriptions/services/subscriptions/subscriptions.api';
import { notificationsApi } from '@/modules/Notifications/services/notifications/notifications.api';
import { profileQuestionsApi } from '@/modules/Questions/services/profile-questions/profile-questions.api';
import { questionSectionsApi } from '@/modules/Questions/services/question-sections/question-sections.api';
import { trustApi } from '@/modules/Trust/services/trust/trust.api';
import { verificationApi } from '@/modules/Verification/services/verification/verification.api';
import { violationsApi } from '@/modules/Violations/services/violations/violations.api';
import { mediaApi } from '@/shared/services/media/media.api';
import { notificationTemplateLookupApi } from '@/shared/services/notification-template-lookup/notification-template-lookup.api';
import { roleLookupApi } from '@/shared/services/role-lookup/role-lookup.api';
import { discountRuleLookupApi } from '@/shared/services/discount-rule-lookup/discount-rule-lookup.api';
import { integrationsLookupApi } from '@/shared/services/integrations-lookup/integrations-lookup.api';
import { paymentsDeskLookupApi } from '@/shared/services/payments-desk-lookup/payments-desk-lookup.api';
import { subscriptionProductLookupApi } from '@/shared/services/subscription-product-lookup/subscription-product-lookup.api';
import { sessionApi } from '@/shared/services/session/session.api';
import { appSettingsApi } from '@/shared/services/app-settings/app-settings.api';
import { settingsApi } from '@/shared/services/settings/settings.api';
import { accountStatusApi } from '@/shared/services/account-status/account-status.api';
import { memberAnswersLookupApi } from '@/shared/services/member-answers-lookup/member-answers-lookup.api';
import { memberConsentsLookupApi } from '@/shared/services/member-consents-lookup/member-consents-lookup.api';
import { userLookupApi } from '@/shared/services/user-lookup/user-lookup.api';
import { registerApi } from '@/shared/store/registry/api-registry/api-registry';
import {
  type ApiSubscriber,
  type RegisteredApi,
} from '@/shared/store/registry/api-registry/api-registry.types';

/**
 * An api that must hear about writes another slice makes (W5, P27c1) is listed
 * with its subscriber; the store wires it at construction.
 */
type ApiEntry = RegisteredApi | { api: RegisteredApi; subscribe: ApiSubscriber };

/** Every api the portal ships — the shared ones plus one line per module. */
const APIS: readonly ApiEntry[] = [
  sessionApi,
  roleLookupApi,
  userLookupApi,
  // The account lifecycle WRITES (P27c1): suspend, restore, ban and restatus,
  // made by the kit's account dialogs on behalf of both the staff page and the
  // members page. It caches nothing; the module lists refresh from the
  // `members.status.changed` it publishes.
  accountStatusApi,
  // The staff read of a member's ANSWERS (P27c1) — the N18 exception on
  // `members.read`, drawn by the kit panel on the members detail and on the
  // verification case, so neither module owns the cache.
  memberAnswersLookupApi,
  // The staff read of a member's legal ACCEPTANCES (P7b), drawn by the kit
  // panel on the members detail; Legal owns the endpoint and may not be
  // imported by Members (W6). Append-only, so it refetches on mount instead
  // of listening to the bus.
  memberConsentsLookupApi,
  // The W6 bridge for the notification CATALOGUE: the Violations rule form reads
  // it for its `legalNoticeTemplateKey` picker, and a module may never import
  // another module's api. One cache, invalidated from the bus when copy is
  // written (`notifications.template.changed`).
  notificationTemplateLookupApi,
  // The W6 bridge for the plan CATALOGUE (P23b2): the order desk offers plans
  // and the orders queue names one a row stored, both by KEY — the handle every
  // consumer outside `modules/Subscriptions` actually holds. Invalidated from
  // the bus on `billing.product.changed`.
  subscriptionProductLookupApi,
  // The W6 bridge for the PAYMENTS DESK (F3): the shell's sidebar badge reads
  // how many member-sent receipts wait for a decision, and the shell may
  // import no module. Invalidated on `billing.order.changed`.
  paymentsDeskLookupApi,
  // The W6 bridge for the DISCOUNT-RULE catalogue (P23b3c). It reads an endpoint
  // behind `courses.read`, which the offer-code screen's own `billing.read`
  // does not imply — so its callers withhold the control rather than fire a
  // request guaranteed to 403 (P21b). Invalidated on `courses.rule.changed`.
  discountRuleLookupApi,
  // The W6 bridge for the INTEGRATIONS overview (P21e3): the Templates page warns
  // when email is switched on with no working provider. Invalidated on
  // `integrations.config.changed`; a probe publishes nothing, so it also
  // refetches on mount.
  integrationsLookupApi,
  mediaApi,
  // Shared rather than module-owned since P10.5d2: a setting whose EDITOR belongs
  // to another module (P10.4's `managedBy`) is read and written from there, and
  // two caches of one resource is the drift W5/W6 exist to prevent.
  settingsApi,
  // The PUBLIC settings read (`GET /app-settings`), shared for the reason
  // `settingsApi` is: `billing.currency` is needed by every surface that quotes
  // money, and it needs no permission — so the amounts stay readable to an
  // operator who cannot read the settings registry at all.
  appSettingsApi,
  authApi,
  rolesApi,
  // The staff list and identity header draw a status the shared account api
  // now writes, so this slice refreshes from the bus (P27c1).
  { api: staffUsersApi, subscribe: subscribeStaffUsers },
  { api: membersApi, subscribe: subscribeMembers },
  integrationsApi,
  questionSectionsApi,
  // `question-options.api.ts` is NOT here: since P10.6d it injects into
  // `profileQuestionsApi` rather than standing as its own root, so that one
  // reducer carries both and an option write can invalidate the question detail
  // the editor's choices table reads.
  profileQuestionsApi,
  trustApi,
  verificationApi,
  moderationApi,
  // ONE api for the queue AND the rule catalogue: editing a rule's action
  // changes what every queue row displays as its configured consequence, and one
  // api's `invalidatesTags` cannot reach another's cache (P17c).
  violationsApi,
  // ONE api for the copy AND the delivery log: a `template_missing` failure is
  // fixed by writing a template, and the operator does both in one sitting —
  // one api's `invalidatesTags` cannot reach another's cache (P17c).
  notificationsApi,
  // ONE api for the whole curriculum — courses, lessons, and (P22b2/P22b3) the
  // tests, rules, grants and attempts. A lesson write moves a course's
  // `lessonCount` and a rule names a course as its source, and one api's
  // `invalidatesTags` cannot reach another's cache (P17c).
  coursesApi,
  // ONE api for the documents, their versions AND the consent audit: publishing
  // a version moves the document's current version, and one api's
  // `invalidatesTags` cannot reach another's cache (P17c).
  legalApi,
  meetingsApi,
  // The subscription catalogue. It stands alone rather than joining a billing
  // super-api: P23b2's direct-order queue reads plans but never writes one, so
  // it bridges through the product LOOKUP and `billing.product.changed` (W5/W6)
  // — the division `user-lookup` already draws for members. The prices are not
  // in here at all; they are a setting, written through `settingsApi`.
  subscriptionsApi,
  // The direct-payment channel (P23b2). Its own api rather than a shared billing
  // one: it reads plans and never writes one, so it bridges to the catalogue
  // through the product LOOKUP and `billing.product.changed` (W5/W6) instead of
  // sharing a cache with it.
  paymentsApi,
  // The store channel's trail (P23b3a). A third billing api rather than a fourth
  // resource on one of the other two: it is read-only, append-only from a
  // webhook, and invalidates nothing either of them holds — so it POLLS instead
  // of subscribing to anything (W5's "live queues poll").
  billingEventsApi,
  // What a member actually holds, from any of the four stores (P23b3b). A fourth
  // billing api for the same reason as the third: the other two are each ONE
  // channel, and this one is the result of all of them — it invalidates nothing
  // either holds, and it hears about their writes on `billing.order.changed`
  // rather than sharing their cache (W5/P17c). Keyed by MEMBER, because the api
  // offers no list.
  entitlementsApi,
  // How an earned discount reaches an IAP price (P23b3c). Its own api rather
  // than part of `subscriptionsApi`: they share a permission and nothing else,
  // and no tag on one can invalidate the other.
  iapOfferCodesApi,
  // The Dashboard's own read of every module's `GET .../stats` (D12) — a
  // bridge api rather than ten module-owned ones, since one page reads all of
  // them together and none of the figures are ever written back through here.
  dashboardStatsApi,
];

/**
 * Called once, before the store is created. Idempotent, because the registry
 * ignores a second registration of the same `reducerPath` — React 19 StrictMode
 * and Vite HMR both re-run module init.
 */
export function registerStoreApis(): void {
  for (const entry of APIS) {
    if ('subscribe' in entry) {
      registerApi(entry.api, { subscribe: entry.subscribe });
    } else {
      registerApi(entry);
    }
  }
}
